import { GatewayImportExportAssujetti } from "@conformite/gateway";
import {
  Button,
  displayErrorToast,
  displayInfoToast,
  displaySucessToast,
  displayWarningToast,
} from "@endya-conformite/ui-kit";
import { GatewayImportExportAssujettiAPI } from "@src/api/importExportAssujetti.api";
import {
  addRegistreFormationDataValidationToSheet,
  downloadSheetFromWorkbook,
  importWorkBookFromFile,
  insertDataInSheet,
} from "@src/helper/excel/excel.export.helper";
import { EntreprisesBySiren } from "@src/store/store.definition";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMillesime } from "@src/store/store.millesime";
import { format } from "date-fns";
import { Worksheet } from "exceljs";
import {
  registreFormationExcelExportTemplate,
  registreFormationExcelTemplate,
} from "../RegistreExcelTemplate";
import { transformAPIRowsForToRows } from "./ExportRegistreExcelMapping";

type ExportAssujettisButtonProps = {
  onExport?: () => void;
};

const hasFormations = (
  data: GatewayImportExportAssujetti.AssujettiRowImport[]
) => {
  return data.some(
    (assujetti) =>
      assujetti.formationContinueIAS.formations.length +
        assujetti.formationContinueIOBSP.formations.length >
      0
  );
};

function processSheet(
  sheet: Worksheet,
  exportData: Array<GatewayImportExportAssujetti.ExportRegistreRowResponse>,
  millesime: number,
  entreprisesBySiren: EntreprisesBySiren
) {
  sheet.name = `${millesime} FORMATION`;
  const transformedData = exportData.flatMap((mapping) =>
    transformAPIRowsForToRows(mapping, entreprisesBySiren)
  );

  insertDataInSheet(
    transformedData,
    sheet,
    registreFormationExcelTemplate,
    registreFormationExcelExportTemplate
  );
  addRegistreFormationDataValidationToSheet(sheet, transformedData.length);
}

export function ExportRegistreButton({
  onExport,
}: ExportAssujettisButtonProps) {
  const { selected: millesime } = useMillesime();
  const { entreprisesBySiren } = useEntreprises();

  const exportRegistre = async () => {
    if (!millesime || !entreprisesBySiren)
      return displayWarningToast(
        "Une erreur est survenue lors de l'export du registre de formations"
      );
    try {
      onExport?.();
      displayInfoToast("Export du registre de formation en cours");
      const workbook = await importWorkBookFromFile(
        "registre-de-formation.xlsx"
      );

      const registre = await GatewayImportExportAssujettiAPI.exportRegistre();

      if (Object.values(registre).flat().length === 0) {
        displayErrorToast("Aucune formation à exporter");
        return undefined;
      }

      let deletedSheets = 0;
      for (let i = 0; i < 3; i += 1) {
        const currentMillesime = millesime - i;
        const currentYearData = registre[currentMillesime] || [];
        const currentSheet = workbook.worksheets[i + 1 - deletedSheets];

        if (hasFormations(currentYearData)) {
          currentSheet.name = `${millesime - i} FORMATION`;
          processSheet(
            currentSheet,
            currentYearData,
            currentMillesime,
            entreprisesBySiren
          );
        } else {
          deletedSheets += 1;
          workbook.removeWorksheet(currentSheet.id);
        }
      }

      await downloadSheetFromWorkbook(
        `Registre_Formation_${format(new Date(), "dd-MM-yyyy_HH:mm")}.xlsx`,
        workbook
      );
      displaySucessToast("Export du registre terminé");
      return undefined;
    } catch (e) {
      console.error(e);
      displayErrorToast(
        "Une erreur est survenue lors de l'export du registre de formations"
      );
      return undefined;
    }
  };

  return (
    <Button
      variant="text"
      onClick={() => {
        void exportRegistre();
      }}
    >
      Exporter le registre de formations
    </Button>
  );
}
